import { z } from "zod";
/**
 * API de eventos v1 — o contrato público.
 *
 * É o que um sistema de fora (o Nexus Members, um checkout, uma loja, o
 * Zapier) manda para `POST /api/v1/events`. O mesmo envelope dos dois lados:
 * quem emite não precisa saber nada do CRM, e o CRM não precisa saber nada de
 * quem emite além do nome da origem.
 *
 * Regras que valem para sempre na v1:
 *  - campo desconhecido é IGNORADO (o zod descarta), para que o emissor possa
 *    acrescentar campos sem quebrar quem recebe;
 *  - `contact` precisa de e-mail ou telefone;
 *  - `order` só é obrigatório nos tipos de pedido.
 */
/** Tipos do catálogo v1. Espelha `EXTERNAL_EVENT_TYPES` do `@rn/core`. */
export const EVENTS_API_TYPES = [
    "lead.captured",
    "checkout.started",
    "checkout.abandoned",
    "payment.pending",
    "payment.failed",
    "order.paid",
    "order.upsell_paid",
    "order.refunded",
    "order.chargeback",
    "subscription.renewed",
    "subscription.canceled",
    "subscription.payment_failed",
    "access.granted",
];
const ORDER_TYPES = [
    "payment.pending",
    "order.paid",
    "order.upsell_paid",
    "order.refunded",
    "order.chargeback",
];
const dataHora = z.string().datetime({ offset: true, message: "Data e hora em ISO 8601" });
const textoCurto = (max) => z.string().trim().min(1).max(max);
const opcional = (schema) => schema.nullish();
export const eventConsentSchema = z.object({
    whatsapp: z.boolean().optional(),
    email: z.boolean().optional(),
    at: opcional(dataHora),
    source: opcional(z.string().trim().max(60)),
});
export const eventContactSchema = z
    .object({
    email: opcional(z.string().trim().email("E-mail inválido").max(200)),
    // E.164 de preferência ("+5571..."). O CRM normaliza; aqui só se barra lixo.
    phone: opcional(z
        .string()
        .trim()
        .max(30)
        .regex(/^\+?[\d\s().-]{8,}$/, "Telefone inválido")),
    name: opcional(z.string().trim().max(200)),
    consent: opcional(eventConsentSchema),
})
    .refine((c) => Boolean(c.email || c.phone), {
    message: "O contato precisa de e-mail ou telefone",
    path: ["email"],
});
export const eventOrderItemSchema = z.object({
    sku: opcional(z.string().trim().max(120)),
    name: textoCurto(200),
    /** main | bump | upsell | downsell. Texto livre para não quebrar com papel novo. */
    role: opcional(z.string().trim().max(20)),
    amount: z.number().nonnegative().max(10_000_000),
});
export const eventOrderSchema = z.object({
    id: textoCurto(200),
    parent_id: opcional(z.string().trim().max(200)),
    offer: opcional(z.string().trim().max(200)),
    items: opcional(z.array(eventOrderItemSchema).max(50)),
    amount: opcional(z.number().nonnegative().max(10_000_000)),
    currency: opcional(z.string().trim().length(3, "Moeda em ISO 4217 (BRL, USD)")),
    /** card | pix | boleto. Texto livre pelo mesmo motivo do `role`. */
    method: opcional(z.string().trim().max(30)),
    pix: opcional(z.object({
        copia_e_cola: opcional(z.string().trim().max(2000)),
        expires_at: opcional(dataHora),
    })),
    checkout_url: opcional(z.string().trim().url("checkout_url precisa ser uma URL").max(2000)),
});
export const eventAttributionSchema = z.object({
    utm_source: opcional(z.string().max(500)),
    utm_medium: opcional(z.string().max(500)),
    utm_campaign: opcional(z.string().max(500)),
    utm_content: opcional(z.string().max(500)),
    utm_term: opcional(z.string().max(500)),
    fbclid: opcional(z.string().max(500)),
    gclid: opcional(z.string().max(500)),
    fbc: opcional(z.string().max(500)),
    fbp: opcional(z.string().max(500)),
});
export const eventEnvelopeSchema = z
    .object({
    event_id: textoCurto(200),
    type: z
        .string()
        .trim()
        .regex(/^[a-z][a-z_]*\.[a-z][a-z_]*$/, "Tipo no formato entidade.acao"),
    version: z.literal(1, { errorMap: () => ({ message: "Esta rota aceita só a versão 1" }) }),
    occurred_at: dataHora,
    source: z.object({
        system: z
            .string()
            .trim()
            .regex(/^[a-z0-9][a-z0-9_.-]{1,39}$/, "Nome de origem em minúsculas, ex.: nexus_member"),
        account: opcional(z.string().trim().max(120)),
    }),
    test: z.boolean().optional().default(false),
    contact: eventContactSchema,
    order: opcional(eventOrderSchema),
    attribution: opcional(eventAttributionSchema),
    data: opcional(z.record(z.string(), z.unknown())),
})
    .superRefine((e, ctx) => {
    if (ORDER_TYPES.includes(e.type) && !e.order) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["order"],
            message: `O tipo ${e.type} exige o pedido (order)`,
        });
    }
    if (e.type === "order.upsell_paid" && e.order && !e.order.parent_id) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["order", "parent_id"],
            message: "Upsell precisa do parent_id do pedido principal",
        });
    }
});
// ── Chaves de API ───────────────────────────────────────────────────────────
/** Escopos que uma chave pode receber. Lista fechada: escopo inventado não vale nada. */
export const API_KEY_SCOPES = ["events:write"];
export const API_KEY_SCOPE_LABEL = {
    "events:write": "Enviar eventos (POST /v1/events)",
};
export const createApiKeySchema = z.object({
    name: z.string().trim().min(2, "Dê um nome à chave").max(80),
    scopes: z.array(z.enum(API_KEY_SCOPES)).min(1).default(["events:write"]),
    /** Origem a que a chave pertence. Ausente = chave da empresa, serve a qualquer origem. */
    sourceSystem: opcional(z
        .string()
        .trim()
        .regex(/^[a-z0-9][a-z0-9_.-]{1,39}$/, "Nome de origem em minúsculas, ex.: nexus_member")),
    expiresAt: opcional(dataHora),
});
// ── Origens de eventos ──────────────────────────────────────────────────────
export const saveEventSourceSchema = z.object({
    label: opcional(z.string().trim().max(80)),
    conversionsHandledBySource: z.boolean().optional(),
    /**
     * Segredo de assinatura (HMAC) gerado na origem. Vazio = não mexer;
     * `clearSigningSecret` remove. Nunca volta em leitura.
     */
    signingSecret: opcional(z.string().trim().max(500)),
    clearSigningSecret: z.boolean().optional(),
});
/** Origens com nome amigável e valores de partida. Espelha `EVENT_SOURCE_PRESETS`. */
export const EVENT_SOURCE_PRESET_OPTIONS = [
    { value: "nexus_member", label: "Nexus Members" },
];
// ── Automação ───────────────────────────────────────────────────────────────
/**
 * Gatilhos que carregam pedido no payload.
 *
 * As condições destes gatilhos leem o pedido (`order.method`, `order.offer`)
 * e não o lead — "Pix pendente" filtra pelo método de pagamento, e oferecer
 * os campos de lead aqui produziria regra que nunca dispara.
 */
export const ORDER_TRIGGERS = [
    "checkout.started",
    "checkout.abandoned",
    "payment.pending",
    "payment.failed",
    "sale.paid",
    "sale.upsell_paid",
    "sale.refunded",
    "subscription.renewed",
    "subscription.canceled",
    "subscription.payment_failed",
    "access.granted",
];
export const ORDER_TRIGGER_FIELDS = [
    { value: "order.offer", label: "Oferta" },
    { value: "order.method", label: "Forma de pagamento (pix, card, boleto)" },
    { value: "order.amount", label: "Valor do pedido" },
    { value: "source", label: "Origem do evento" },
    { value: "reason", label: "Motivo do estorno (refund, chargeback)" },
    { value: "fullyRefunded", label: "Estorno zerou a venda (true/false)" },
];
/**
 * Variáveis do pedido nas mensagens, nos gatilhos de checkout e venda.
 * Saem do payload do evento que disparou a automação.
 */
export const ORDER_TEMPLATE_VARIABLES = [
    { value: "{{order.offer}}", label: "Oferta" },
    { value: "{{order.amountFormatted}}", label: "Valor (R$ 97,00)" },
    { value: "{{order.pixCode}}", label: "Pix copia e cola" },
    { value: "{{order.pixExpiresAt}}", label: "Validade do Pix" },
    { value: "{{order.checkoutUrl}}", label: "Link para retomar o checkout" },
    { value: "{{order.method}}", label: "Forma de pagamento" },
];
//# sourceMappingURL=events-api.js.map